import { UIManager } from "./UIManager";
import { ViewModeManager } from "./ViewModeManager";

/**
 * Interface defining the contract for a managers module.
 */
export interface IManagersModule<TViewModel> {
    readonly viewModel: TViewModel;
    dispose(): void;
    reinitialize(): void;
}

/**
 * Abstract base class for creating a module that manages multiple manager instances.
 * Provides lifecycle management including initialization, disposal, and reinitialization.
 * @template TViewModel The type of the view model that the managers will operate on
 */
export abstract class ManagersModuleBase<TViewModel> implements IManagersModule<TViewModel> {
    protected params?: any;
    /** Flag indicating whether the module has been initialized */
    protected _initialized: boolean = false;

    // Common managers available to all derived classes
    public ui!: UIManager;
    public viewMode!: ViewModeManager;

    /**
     * Creates a new managers module instance.
     * @param viewModel The view model instance that managers will interact with
     */
    constructor(public readonly viewModel: TViewModel, params?: any) {
        this.params = params;
        this.initialize();
    }

    /**
     * Abstract method that derived classes must implement to create their specific managers.
     * This method is called during initialization.
     */
    protected abstract createManagers(params?: any): void;

    /**
     * Initializes the module by creating all managers.
     * Ensures initialization only happens once.
     * @private
     */
    private initialize(): void {
        if (this._initialized) return;

        this.createCommonManagers();
        this._initialized = true;
    }

    /**
     * Creates the common managers that are available to all derived classes.
     * @protected
     */
    protected createCommonManagers(): void {
        this.ui = new UIManager();
        this.viewMode = new ViewModeManager();
    }

    /**
     * Disposes of the module and all its managers.
     * Calls the onDispose hook for cleanup and marks the module as uninitialized.
     */
    public dispose(): void {
        this.onDispose();
        this.onDisposeCommon();
        this._initialized = false;
    }

    /**
     * Hook method for derived classes to perform custom cleanup logic.
     * Override this method to dispose of specific managers or resources.
     * @protected
     */
    protected onDispose(): void {
        // Override in derived classes for cleanup
    }

    /**
     * Disposes of common managers and resets them to initial state.
     * @protected
     */
    protected onDisposeCommon(): void {
        this.ui = new UIManager();
        this.viewMode = new ViewModeManager();
    }

    /**
     * Disposes of the current managers and recreates them.
     * Useful for resetting the module state or applying configuration changes.
     */
    public reinitialize(): void {
        this.dispose();
        this.initialize();
        this.createManagers(this.params);
    }
}
